import { Button, Input, Label, Textarea } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FieldError } from '@/components/ui/field-error';
import { MessagePreview } from '@/features/notifications/message-preview';
import { NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, useCreateNotificationMessage, useUpdateNotificationMessage } from '@/hooks/queries/notifications/use-notification-messages';
import { getErrorMessage, HTTP_CONFLICT, isHttpStatus } from '@/lib/api/error';
import { BODY_MAX_LENGTH, LINK_MAX_LENGTH, type NotificationMessage, TITLE_MAX_LENGTH } from '@/services/api/notifications';

// Mirrors bop-api's content.js: invisible characters would let approved text differ from
// what the client reads
// biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
const NO_HIDDEN_CHARACTERS_REGEX = /^[^\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u200b-\u200f\u202a-\u202e\u2066-\u2069]*$/;

const HIDDEN_CHARACTERS_MESSAGE = 'Contains invisible characters — retype rather than pasting';

const composeSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(TITLE_MAX_LENGTH, `Max ${TITLE_MAX_LENGTH} characters`).regex(NO_HIDDEN_CHARACTERS_REGEX, HIDDEN_CHARACTERS_MESSAGE),
  body: z.string().trim().min(1, 'Body is required').max(BODY_MAX_LENGTH, `Max ${BODY_MAX_LENGTH} characters`).regex(NO_HIDDEN_CHARACTERS_REGEX, HIDDEN_CHARACTERS_MESSAGE),
  link: z
    .string()
    .trim()
    .max(LINK_MAX_LENGTH, `Max ${LINK_MAX_LENGTH} characters`)
    .regex(NO_HIDDEN_CHARACTERS_REGEX, HIDDEN_CHARACTERS_MESSAGE)
    // Opened by every client's service worker - anything but a relative path is a phishing link
    .refine((link) => link === '' || (link.startsWith('/') && !link.startsWith('//')), 'Must be a relative path starting with /'),
});

type ComposeSchema = z.infer<typeof composeSchema>;

const EMPTY_FORM: ComposeSchema = { title: '', body: '', link: '' };

interface ComposeCardProps {
  /** null → composing a new draft; set → amending an existing message */
  editing: NotificationMessage | null;
  onDone: () => void;
}

export const ComposeCard = ({ editing, onDone }: ComposeCardProps) => {
  const form = useForm<ComposeSchema>({ resolver: zodResolver(composeSchema), mode: 'onChange', defaultValues: EMPTY_FORM });
  const queryClient = useQueryClient();
  const createMessage = useCreateNotificationMessage();
  const updateMessage = useUpdateNotificationMessage();

  const [title, body, link] = form.watch(['title', 'body', 'link']);

  useEffect(() => {
    form.reset(editing ? { title: editing.title, body: editing.body, link: editing.link ?? '' } : EMPTY_FORM);
  }, [editing, form]);

  const handleSave = form.handleSubmit(async (values) => {
    const request = { title: values.title, body: values.body, ...(values.link ? { data: { link: values.link } } : {}) };
    try {
      await (editing ? updateMessage(editing.messageId, request) : createMessage(request));
      toast.success(editing ? 'Message updated' : 'Draft saved');
      form.reset(EMPTY_FORM);
      onDone();
    } catch (error) {
      if (isHttpStatus(error, HTTP_CONFLICT)) {
        toast.error('The message changed while you were editing — refreshing.');
        await queryClient.invalidateQueries({ queryKey: NOTIFICATION_MESSAGE_LISTS_QUERY_KEY });
        return;
      }
      toast.error(`Failed to save message: ${getErrorMessage(error)}`);
    }
  });

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <form className="flex flex-col gap-4" noValidate onSubmit={handleSave}>
          <h2 className="text-sm font-semibold">{editing ? 'Amend message' : 'Compose message'}</h2>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notification-title">Title</Label>
            <Input id="notification-title" maxLength={TITLE_MAX_LENGTH} {...form.register('title')} />
            <p className="text-xs text-muted-foreground">
              {title.trim().length}/{TITLE_MAX_LENGTH} characters
            </p>
            <FieldError message={form.formState.errors.title?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notification-body">Body</Label>
            <Textarea id="notification-body" maxLength={BODY_MAX_LENGTH} rows={4} {...form.register('body')} />
            <p className="text-xs text-muted-foreground">
              {body.trim().length}/{BODY_MAX_LENGTH} characters
            </p>
            <FieldError message={form.formState.errors.body?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notification-link">Link (optional, in-app path)</Label>
            <Input id="notification-link" maxLength={LINK_MAX_LENGTH} placeholder="/announcements/…" {...form.register('link')} />
            <FieldError message={form.formState.errors.link?.message} />
          </div>
          <div className="flex items-center gap-2">
            <Button disabled={!form.formState.isValid || form.formState.isSubmitting} type="submit" variant="primary">
              {editing ? 'Save changes' : 'Save draft'}
            </Button>
            {editing ? (
              <Button onClick={onDone} type="button" variant="outline">
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
        <MessagePreview body={body} link={link} title={title} />
      </div>
    </section>
  );
};
ComposeCard.displayName = 'ComposeCard';
